import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, DollarSign, Search, X, Paperclip } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { TransactionForm } from '../components/TransactionForm';
import { CambioForm } from '../components/CambioForm';
import {
  Transaction,
  getTransactions,
  getDeclaredAccounts,
  createTransaction,
  updateTransaction,
  deleteTransaction,
} from '../services/apiService';
import { uploadDocument, getTransactionDocuments, downloadDocument, DocumentEntry } from '../services/documentService';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

type FormMode = 'none' | 'create' | 'edit' | 'cambio';

export default function TransactionsPage() {
  const { canWrite, isAdmin } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [formMode, setFormMode] = useState<FormMode>('none');
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [docPopover, setDocPopover] = useState<string | null>(null);
  const [docPopoverDocs, setDocPopoverDocs] = useState<DocumentEntry[]>([]);

  // Filters
  const [filterBegin, setFilterBegin] = useState('');
  const [filterEnd, setFilterEnd] = useState('');
  const [filterAccount, setFilterAccount] = useState('');
  const [filterDescription, setFilterDescription] = useState('');

  const loadData = useCallback(async (signal?: AbortSignal) => {
    try {
      const params: Record<string, string> = {};
      if (filterBegin) params.begin = filterBegin;
      if (filterEnd) params.end = filterEnd;
      if (filterAccount) params.account = filterAccount;
      if (filterDescription) params.description = filterDescription;

      const [xactRes, accRes] = await Promise.all([
        getTransactions(params, signal),
        getDeclaredAccounts(),
      ]);
      if (!signal?.aborted) {
        setTransactions(xactRes.transactions);
        setAccounts(accRes.accounts);
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.error('Failed to load transactions', err);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [filterBegin, filterEnd, filterAccount, filterDescription]);

  useEffect(() => {
    setLoading(true);
    const controller = new AbortController();
    const timer = setTimeout(() => {
      loadData(controller.signal);
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [loadData]);

  const uploadFilesForTransaction = async (files: File[], transactionDate: string, transactionPayee: string) => {
    for (const file of files) {
      try {
        await uploadDocument(file, 'comprovante', '', transactionDate, transactionPayee);
      } catch (err) {
        console.warn('Failed to upload document:', file.name, err);
      }
    }
  };

  const handleCreate = async (data: any, pendingFiles?: File[]) => {
    await createTransaction(data);
    if (pendingFiles?.length) {
      const txnDate = data.date.replace(/\//g, '-');
      await uploadFilesForTransaction(pendingFiles, txnDate, data.payee);
    }
    setFormMode('none');
    setLoading(true);
    await loadData();
  };

  const handleUpdate = async (data: any, pendingFiles?: File[]) => {
    if (!editingTransaction) return;
    await updateTransaction(editingTransaction.id, data);
    if (pendingFiles?.length) {
      const txnDate = data.date.replace(/\//g, '-');
      await uploadFilesForTransaction(pendingFiles, txnDate, data.payee);
    }
    setFormMode('none');
    setEditingTransaction(null);
    setLoading(true);
    await loadData();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este lançamento?')) return;
    setDeleting(id);
    try {
      await deleteTransaction(id);
      setLoading(true);
      await loadData();
    } catch (err) {
      console.error('Failed to delete transaction', err);
    } finally {
      setDeleting(null);
    }
  };

  const openEdit = (xact: Transaction) => {
    setEditingTransaction(xact);
    setFormMode('edit');
  };

  const handleDocClick = async (xactId: string) => {
    if (docPopover === xactId) {
      setDocPopover(null);
      return;
    }
    try {
      const res = await getTransactionDocuments(xactId);
      setDocPopoverDocs(res.documents);
      setDocPopover(xactId);
    } catch {
      console.error('Failed to load documents for transaction', xactId);
    }
  };

  const handleDocDownload = async (key: string) => {
    try {
      const blob = await downloadDocument(key);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = key.split('/').pop() || 'document';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      console.error('Failed to download document');
    }
  };

  const formatBRL = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const initialLoading = loading && transactions.length === 0 && !filterAccount && !filterDescription;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Lançamentos</h1>
        {canWrite && (
          <div className="flex gap-2">
            <button
              onClick={() => { setFormMode('cambio'); setEditingTransaction(null); }}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
            >
              <DollarSign className="h-4 w-4" /> Câmbio
            </button>
            <button
              onClick={() => { setFormMode('create'); setEditingTransaction(null); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" /> Novo Lançamento
            </button>
          </div>
        )}
      </div>

      {formMode !== 'none' && (
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">
              {formMode === 'cambio'
                ? 'Novo Recebimento do Exterior'
                : formMode === 'edit'
                ? 'Editar Lançamento'
                : 'Novo Lançamento'}
            </h2>
            <button
              onClick={() => { setFormMode('none'); setEditingTransaction(null); }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {formMode === 'cambio' ? (
            <CambioForm
              accounts={accounts}
              onSubmit={handleCreate}
              onCancel={() => setFormMode('none')}
            />
          ) : (
            <TransactionForm
              accounts={accounts}
              initial={formMode === 'edit' ? editingTransaction : null}
              onSubmit={formMode === 'edit' ? handleUpdate : handleCreate}
              onCancel={() => { setFormMode('none'); setEditingTransaction(null); }}
            />
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Search className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">Filtros</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="date"
            value={filterBegin}
            onChange={(e) => setFilterBegin(e.target.value)}
            placeholder="Data início"
            className="border rounded-lg px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={filterEnd}
            onChange={(e) => setFilterEnd(e.target.value)}
            placeholder="Data fim"
            className="border rounded-lg px-3 py-2 text-sm"
          />
          <input
            type="text"
            value={filterAccount}
            onChange={(e) => setFilterAccount(e.target.value)}
            placeholder="Conta"
            className="border rounded-lg px-3 py-2 text-sm"
          />
          <input
            type="text"
            value={filterDescription}
            onChange={(e) => setFilterDescription(e.target.value)}
            placeholder="Descrição"
            className="border rounded-lg px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* Transaction list */}
      {initialLoading ? <LoadingSpinner /> :
      loading ? <div className="text-center py-12 text-gray-500">Carregando...</div> :
      <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Data</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Descrição</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Postings</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Tags</th>
                {canWrite && (
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Ações</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y">
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan={canWrite ? 5 : 4} className="text-center py-8 text-gray-500 text-sm">
                    Nenhum lançamento encontrado.
                  </td>
                </tr>
              ) : (
                transactions.map((xact) => (
                  <tr key={xact.id} className="hover:bg-blue-50 even:bg-gray-100">
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {xact.date}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      <div className="flex items-center gap-1.5">
                        {xact.payee}
                        {(xact.doc_count ?? 0) > 0 && (
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => handleDocClick(xact.id)}
                              className="text-blue-500 hover:text-blue-700"
                              title={`${xact.doc_count} documento(s)`}
                            >
                              <Paperclip className="h-3.5 w-3.5" />
                            </button>
                            {docPopover === xact.id && (
                              <div className="absolute z-10 left-0 top-6 bg-white border rounded-lg shadow-lg p-2 min-w-48">
                                <div className="text-xs text-gray-500 mb-1">{docPopoverDocs.length} documento(s)</div>
                                {docPopoverDocs.map((doc) => (
                                  <button
                                    key={doc.key}
                                    onClick={() => handleDocDownload(doc.key)}
                                    className="block w-full text-left text-xs text-blue-600 hover:bg-blue-50 rounded px-2 py-1 truncate"
                                  >
                                    {doc.key.split('/').pop()}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {xact.postings.map((p, i) => (
                        <div key={i} className="flex justify-between gap-4">
                          <span className="truncate">{p.account}</span>
                          <span className={`tabular-nums whitespace-nowrap ${(p.amount ?? 0) < 0 ? 'text-red-600' : 'text-green-700'}`}>
                            {formatBRL(p.amount ?? 0)}
                          </span>
                        </div>
                      ))}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {Object.entries(xact.tags || {}).map(([k, v]) => (
                        <span
                          key={k}
                          className="inline-block bg-gray-100 rounded-full px-2 py-0.5 text-xs mr-1 mb-1"
                        >
                          {k}: {v}
                        </span>
                      ))}
                    </td>
                    {canWrite && (
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => openEdit(xact)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                          title="Editar"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => handleDelete(xact.id)}
                            disabled={deleting === xact.id}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded ml-1"
                            title="Excluir"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t bg-gray-50 text-sm text-gray-500">
          {transactions.length} lançamento{transactions.length !== 1 ? 's' : ''}
        </div>
      </div>}
    </div>
  );
}
